// Design system domain types
// All types related to design system tokens, categories, and content structure

// Design system section type (for editor navigation and updates)
export type DesignSystemSection =
	| 'foundations'
	| 'theme'
	| 'styles'
	| 'components'
	| 'gallery'
	| 'guidelines'
	| 'assets';

// Minimal info needed to open a DsTab: display name + optional contentPath
export interface DsLink {
	label: string;
	contentPath?: string;
	deleted?: boolean;
}

// Valid design system sections array (for validation)
export const VALID_DESIGN_SYSTEM_SECTIONS: DesignSystemSection[] = [
	'foundations',
	'theme',
	'styles',
	'components',
	'gallery',
	'guidelines',
	'assets',
];

// Formal mapping from CSS comment text to TypeScript category type values
export const CATEGORY_COMMENT_MAP = {
	Colors: 'color',
	Typography: 'typography',
	Spacing: 'spacing',
	Elevation: 'elevation',
	'Border Radius': 'borderRadius',
	Opacity: 'opacity',
	Motion: 'motion',
	Other: 'other',
} as const;

export type TokenCategory = (typeof CATEGORY_COMMENT_MAP)[keyof typeof CATEGORY_COMMENT_MAP];

// Formal mapping from CSS comment text to Typography subcategory type values
export const TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP = {
	'Font Families': 'fontFamilies',
	'Font Sizes': 'fontSizes',
	'Line Heights': 'lineHeights',
	'Font Weights': 'fontWeights',
} as const;

export type TypographySubcategory =
	(typeof TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP)[keyof typeof TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP];

// Formal mapping from CSS comment text to Color subcategory type values
export const COLOR_SUBCATEGORY_COMMENT_MAP = {
	'Page + surfaces': 'pageSurfaces',
	Text: 'text',
	'Accent / brand': 'accentBrand',
	Semantic: 'semantic',
	Overlay: 'overlay',
} as const;

export type ColorSubcategory =
	(typeof COLOR_SUBCATEGORY_COMMENT_MAP)[keyof typeof COLOR_SUBCATEGORY_COMMENT_MAP];

// Union of all subcategories
export type TokenSubcategory = TypographySubcategory | ColorSubcategory | string;

// Base token value interface
export interface BaseTokenValue {
	kind: string;
	original: string; // Raw CSS value, e.g., "#f8fbff" or "0.5rem"
}

// Specific token value types extending BaseTokenValue
export interface ColorTokenValue extends BaseTokenValue {
	kind: 'color';
	hex: string;
	rgb: { r: number; g: number; b: number; a?: number };
	hsl?: { h: number; s: number; l: number; a?: number };
}

export interface LengthTokenValue extends BaseTokenValue {
	kind: 'length';
	rem?: string;
	px?: number;
	em?: string;
	unit: 'rem' | 'px' | 'em' | 'pt' | 'pc' | 'in' | 'cm' | 'mm';
	numericValue: number;
}

export interface ShadowTokenValue extends BaseTokenValue {
	kind: 'shadow';
	offsetX: number;
	offsetY: number;
	blur: number;
	spread?: number;
	color: ColorTokenValue;
}

export interface FontFamilyTokenValue extends BaseTokenValue {
	kind: 'fontFamily';
	families: string[]; // e.g., ["system-ui", "Inter", "sans-serif"]
}

export interface FontSizeTokenValue extends BaseTokenValue {
	kind: 'fontSize';
	length: LengthTokenValue; // Font sizes are lengths
}

export interface LineHeightTokenValue extends BaseTokenValue {
	kind: 'lineHeight';
	numeric?: number; // e.g., 1.5
	length?: LengthTokenValue; // e.g., "24px"
}

export interface FontWeightTokenValue extends BaseTokenValue {
	kind: 'fontWeight';
	numeric: number; // e.g., 400, 500, 600, 700
	keyword?: 'normal' | 'bold' | 'lighter' | 'bolder';
}

export interface BorderRadiusTokenValue extends BaseTokenValue {
	kind: 'borderRadius';
	length: LengthTokenValue; // Border radius values are lengths
}

export interface OpacityTokenValue extends BaseTokenValue {
	kind: 'opacity';
	value: number; // 0-1
}

export interface TransitionTokenValue extends BaseTokenValue {
	kind: 'transition';
	duration: string; // e.g., "0.16s"
	timingFunction: string; // e.g., "ease-out"
	property?: string;
	delay?: string;
}

export interface NumberTokenValue extends BaseTokenValue {
	kind: 'number';
	value: number;
}

export interface StringTokenValue extends BaseTokenValue {
	kind: 'string';
}

// Discriminated union of all token value types
export type TokenValue =
	| ColorTokenValue
	| LengthTokenValue
	| ShadowTokenValue
	| FontFamilyTokenValue
	| FontSizeTokenValue
	| LineHeightTokenValue
	| FontWeightTokenValue
	| BorderRadiusTokenValue
	| OpacityTokenValue
	| TransitionTokenValue
	| NumberTokenValue
	| StringTokenValue;

// Main token interface - stores value directly (no separate valueType or metadata)
export interface DesignSystemToken {
	id: string; // e.g., "color-bg"
	name: string; // e.g., "Color Background" (generated from CSS variable name)
	cssVariable: string; // e.g., "--ds-color-bg"
	category: TokenCategory; // From CSS comment (may be wrong - use value.kind for display)
	subcategory?: TokenSubcategory; // From CSS comment
	value: TokenValue; // Structured value with kind field (determines how to display)
	mode?: string; // e.g. "dark" when from theme/colors.dark.css; undefined = base/default
}

// Component prop interface
export interface ComponentProp {
	name: string; // e.g., "label", "variant"
	type: 'string' | 'number' | 'boolean' | 'json' | 'select' | 'icon';
	defaultValue?: string; // Default value from component code
	description?: string; // From comments or inferred
	options?: string[]; // For select/boolean types
	required?: boolean; // Whether prop is required
}

// Component category type
export type ComponentCategory =
	| 'button'
	| 'card'
	| 'form'
	| 'navigation'
	| 'data-display'
	| 'other';

// Main component interface - stores web component information
export interface DesignSystemComponent {
	id: string; // e.g., "ds-button"
	name: string; // Derived from path via displayNameFromPath — do not set from content
	tagName: string; // e.g., "ds-button"
	category: ComponentCategory; // e.g., "button", "card", "form"
	props: ComponentProp[]; // Parsed component attributes/props
	exampleHTML?: string; // Example usage HTML
	exampleProps?: Record<string, any>; // Showcase prop values from .example.json
	children?: string; // Example children HTML from .example.json
	variants?: string[]; // Derived from prop named 'variant' with type 'select'
	sizes?: string[]; // Derived from prop named 'size' with type 'select'
	sourceCode?: string; // Full source code for this component (class + define)
	cssCode?: string; // CSS code for this component
	guidelines?: string; // Markdown description of how to use this component
}

// Recipe property (a single CSS declaration within a recipe class)
export interface DesignSystemRecipeProperty {
	property: string; // e.g., "font-family"
	value: string; // e.g., "var(--theme-font-pitch-sans-medium)"
}

// Recipe interface - a reusable .ds-* CSS class from styles/recipes/
export interface DesignSystemRecipe {
	id: string; // e.g., "type-heading-lg"
	className: string; // e.g., ".ds-type-heading-lg"
	name: string; // e.g., "Type Heading Lg" (display name)
	domain: string; // e.g., "type" (derived from filename or class name)
	description?: string; // From preceding CSS comment
	properties: DesignSystemRecipeProperty[];
	sourcePath: string; // e.g., "styles/recipes/type.css"
}

// Per-DS view preferences persisted to localStorage
export interface DsViewPreferences {
	colorsViewMode?: 'grid' | 'list';
	colorsGridVisible?: boolean;
	componentsViewMode?: 'grid' | 'list';
	assetsViewMode?: 'grid' | 'list';
}

// Design system gallery item interface - represents a gallery entry/example for the design system
export interface DesignSystemGalleryItem {
	path: string; // File path (e.g., "gallery/button-variants.html")
	title: string; // Derived from path via displayNameFromPath — do not set from content
	code: string;
	isValidHtml?: boolean; // Flag indicating if the code contains valid HTML tags
}
